import { memo, useMemo } from 'react';

import { isDefaultDeckSurfaceHex } from '../../../deck/defaults';
import { FolderClosed } from '../../icons/lucide/FolderClosed';
import { brandColor } from '../../styles/brand-colors';
import { useTheme } from '../../theme';
import { mixHex } from '../../utils/color';
import { FolderLikeCard } from '../cards/FolderLikeCard';
import type { FolderCardProps } from './FolderCard.types';

/** The design's sample folder blue. */
const DEFAULT_FOLDER_COLOR = '#60a5fa';

/**
 * Cross-platform FolderCard — the DS card for a folder of decks. Renders
 * identically on web and React Native. See {@link FolderCardProps}.
 *
 * @example
 * ```tsx
 * import { FolderCard } from '@flashka/shared/ui';
 *
 * <FolderCard
 *   title="Anatomy"
 *   color="#9ae600"
 *   previews={decks.slice(0, 3).map((d) => ({ image: d.previewUrl, emoji: d.emoji }))}
 *   badges={[
 *     { count: 6, icon: Layers, label: t('folder.decks', { count: 6 }) },
 *     { count: 120, icon: Sparkles, label: t('folder.cards', { count: 120 }) },
 *   ]}
 *   onPress={openFolder}
 *   onActionsPress={openMenu}
 * />
 * ```
 */
function FolderCardImpl({ color = DEFAULT_FOLDER_COLOR, ...props }: FolderCardProps) {
  // List glyph: a colour-filled folder (fill = identity colour, stroke a
  // darker shade — same two-tone treatment as the folder shape). A folder with
  // NO colour arrives as the card surface itself (`secondary-100`), so filling
  // with it vanished into the card — 1.05:1 in dark. It draws outline-only
  // instead, the neutral rule `DeckIcon` already follows.
  //
  // Memoized because it is a SLOT: a fresh element here would be a changed prop
  // on every render, which is exactly what turns `FolderLikeCard`'s own memo
  // off. It depends on nothing but the colour and the theme.
  const { isDark } = useTheme();
  const listGlyph = useMemo(
    () =>
      isDefaultDeckSurfaceHex(color) ? (
        <FolderClosed color={brandColor('secondary', '600', isDark)} size={20} />
      ) : (
        <FolderClosed color={mixHex(color, 'black', 0.2)} fill={color} size={20} />
      ),
    [color, isDark],
  );
  return <FolderLikeCard {...props} color={color} listGlyph={listGlyph} />;
}

/** See `FolderLikeCard`'s note on why the DS cards are memoized. */
export const FolderCard = memo(FolderCardImpl);
(FolderCard as { displayName?: string }).displayName = 'FolderCard';
